import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Row, Col, Form } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import classNames from "classnames";

import {
  continuePremiumPlan,
  subscribeToPremiumPlan,
} from "features/Plans/service";
import {
  parseErrorMessage,
  openNewTab,
  numberSeparator,
  roundOff,
} from "utils";

import { usePlans } from "features/Plans/hook";
import { Tag } from "components/Tag";
import { toast } from "Layout/slice";
import { Button } from "components/Button";
import { Loader } from "components/Loader";
// import { PLANS_LINK } from "utils/constants";
import { useNavigation } from "utils/navigate";
import { fetchUserDetails } from "Layout/slice/thunk";
import { userDetailSelect } from "Layout/slice/selector";
import { FULL_MONTH_YEAR_FORMAT, unixToDateTime } from "utils/date";
import {
  PurchasePlanCardContainer,
  BillingBackgroundIcon as BillingBackgroundIconStyled,
} from "./PurchasePlanCard.styles";
import {
  ClockRefreshIcon,
  PercentageIcon,
  IconGift,
  BillingBackgroundIcon,
} from "assets/icons";
import ConfirmationModal from "shared/Modal/ConfirmationModal/index";
import { iconCancelModal } from "assets/icons/index";
import { UnsubscribePlanModal } from "shared/Modal/UnsubscribePlanModal/index";
import { PurchasePlanModalPlaceholder } from "./PurchasePlanCardPlaceholder";
import { ChoosePlanModal } from "features/Account/v2/ChoosePlanModal/index";
import { contains } from "utils";
import { trackEvent } from "utils/tracking";

const PurchasePlanCard = ({
  isChangePlan,
  isCancelPlan,
  isAccountPage,
  // handleExplore,
}) => {
  const dispatch = useDispatch();
  const userDetails = useSelector(userDetailSelect);
  const currentPlan = userDetails?.surmount_plan;

  const { isLoading, plans } = usePlans();

  const [fetching, setFetching] = useState(false);
  const [duration, setDuration] = useState("free");
  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [isUrlFetching, setIsUrlFetching] = useState(false);
  const [isStopping, setStopping] = useState(false);
  const [showCancelConfirmModal, setShowCancelConfirmModal] = useState(false);
  const [showCancelSubscriptionModal, setShowCancelSubscriptionModal] =
    useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [choosePlaneModel, setChoosePlaneModel] = useState(false);

  const { navigateToDashboard } = useNavigation();

  const selectPlanForBillingCycle = useCallback(
    (newBillingCycle) => {
      if (currentPlan && currentPlan.interval === newBillingCycle) {
        if (contains(currentPlan.name.toLowerCase(), "premium")) {
          const newPlanId = currentPlan.id.replace("premium", "pro");
          setSelectedPlanId(newPlanId);
        } else {
          setSelectedPlanId(currentPlan.id);
        }

        setDuration(newBillingCycle);
      } else {
        const freePlan = Object.values(plans).find((plan) => plan.price === 0);
        if (freePlan) {
          setDuration("free");
          setSelectedPlanId(freePlan.id);
        } else {
          setDuration(newBillingCycle);
          setSelectedPlanId(null);
        }
      }
    },
    [currentPlan, plans]
  );

  const toggleSnaptradeModal = () => {
    setChoosePlaneModel((prevState) => !prevState);
  };

  const isCancelledPlan =
    userDetails?.stripe_subscription?.status === "canceled";

  const isFreeUserType = userDetails?.user_type === "FREE";
  const stripeSubscription = userDetails?.stripe_subscription;

  const billingCycles = useMemo(() => {
    const intervals = Object.values(plans)
      .map((plan) => plan.interval)
      .filter((interval) => interval);

    return Array.from(new Set(intervals)).sort((a, b) => {
      if (a === "monthly") return -1;
      if (b === "monthly") return 1;
      return a.localeCompare(b);
    });
  }, [plans]);

  useEffect(() => {
    if (userDetails && plans && Object.keys(plans).length > 0) {
      if (currentPlan && currentPlan.interval) {
        setBillingCycle(currentPlan.interval);
        selectPlanForBillingCycle(currentPlan.interval);
      } else {
        setBillingCycle("monthly");
        selectPlanForBillingCycle("monthly");
      }
    }
  }, [userDetails, currentPlan, plans]);

  const filteredPlans = useMemo(() => {
    return Object.values(plans)
      .filter(
        (plan) => plan.interval === billingCycle || plan.interval === null
      )
      .sort((a, b) => a.price - b.price);
  }, [plans, billingCycle]);

  const selectedPlan = useMemo(() => {
    if (currentPlan && contains(currentPlan.name.toLowerCase(), "premium")) {
      return {
        ...currentPlan,
        id: currentPlan.id.replace("premium", "pro"),
      };
    }

    if (selectedPlanId && Object.values(plans).length > 0) {
      return Object.values(plans).find((plan) => plan.id === selectedPlanId);
    }

    return null;
  }, [plans, selectedPlanId, currentPlan]);

  const handleDurationchange = (e) => {
    const { value } = e.target;

    const [planId, interval] = value.split("-");
    setSelectedPlanId(planId);
    setDuration(interval);
  };

  const handleBillingCycleChange = (e) => {
    const { value } = e.target;
    setBillingCycle(value);
    selectPlanForBillingCycle(value);
  };

  const handlePurchasePremium = async () => {
    if (currentPlan?.id === selectedPlan?.id) return;

    try {
      trackEvent({
        name: "choose_plan",
        label: "Plans",
        plan_id: selectedPlan?.id,
        interval: duration,
        price: selectedPlan?.price,
      });
      setFetching(true);

      const params = {
        plan: selectedPlan?.id,
        interval: duration,
      };
      const response = await subscribeToPremiumPlan(params);
      if (response.url) {
        trackEvent({
          name: "start_subscription",
          label: "Plans",
          plan_id: selectedPlan?.id,
          interval: duration,
          price: selectedPlan?.price,
        });
        openNewTab(response.url, "_self");
      } else {
        trackEvent({
          name: "order_complete",
          label: "Plans",
          plan_id: selectedPlan?.id,
          interval: duration,
          price: selectedPlan?.price,
        });
        dispatch(fetchUserDetails());
        dispatch(
          toast({
            type: "info",
            body: response.message,
          })
        );
      }
    } catch (error) {
      const errorMessage = parseErrorMessage(error);
      trackEvent({
        name: "payment_failed",
        label: "Plans",
        plan_id: selectedPlan?.id,
        error: errorMessage,
      });
      dispatch(
        toast({
          type: "error",
          body: errorMessage,
        })
      );
    } finally {
      setFetching(false);
    }
  };

  const handleChangePremium = () => {
    toggleSnaptradeModal();
  };

  const toggleCancelModal = () => {
    setShowCancelConfirmModal(!showCancelConfirmModal);
  };

  const toggleCancelSubscriptionModal = () => {
    setShowCancelConfirmModal(false);
    setShowCancelSubscriptionModal(!showCancelSubscriptionModal);
  };

  const getActionButtonText = useCallback(() => {
    if (isFreeUserType && duration !== "free" && duration !== undefined) {
      return `Start My Plan`;
    }

    if (currentPlan?.id !== selectedPlan?.id && !isFreeUserType) {
      return "Change Plan";
    }

    if (isCancelledPlan) {
      return "Purchase";
    }

    if (isChangePlan) {
      return "Change Plan";
    }

    if (isFreeUserType && userDetails?.trial_days === 0) {
      return "Upgrade Now";
    }

    return `Start My Plan`;
  }, [
    isFreeUserType,
    isCancelledPlan,
    isChangePlan,
    userDetails,
    duration,
    currentPlan,
    selectedPlan,
  ]);

  const handleContinuePremiumPlan = async () => {
    try {
      setIsUrlFetching(true);
      await continuePremiumPlan();
      dispatch(fetchUserDetails());
      navigateToDashboard();
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsUrlFetching(false);
    }
  };

  const getCancelPlanUI = () => {
    const stripeSubscription = userDetails?.stripe_subscription;
    const price = stripeSubscription?.items?.data[0]?.price.unit_amount / 100;
    const formattedPrice = numberSeparator(roundOff(price));

    // Calculate discount percentage if applicable
    const discountPercent = currentPlan?.yearly_badge_message || null;

    return (
      <Col md={12} className="current-subscription-wrapper">
        {/* Header with Plan Name and Badge */}
        <div className="plan-name-wrapper">
          <div className="subscription-header">
            <div className="plan-title-section">
              <h2 className="plan-name">
                {currentPlan?.name || "Surmount Pro"}
              </h2>
              <Tag
                label={
                  currentPlan?.interval?.charAt(0).toUpperCase() +
                    currentPlan?.interval?.slice(1) || "Yearly"
                }
              />
            </div>
          </div>

          {/* Price Display */}
          <div className="price-display">
            <span className="price-amount">${formattedPrice}</span>
            <span className="price-currency">USD</span>
          </div>
        </div>

        {/* Billing Background SVG */}
        <BillingBackgroundIconStyled>
          <BillingBackgroundIcon />
        </BillingBackgroundIconStyled>

        {/* Info Cards Row */}
        <div className="info-cards-row-wrapper">
          <div className="info-cards-row">
            <div className="info-card">
              <div className="info-card-content">
                <div className="info-icon">
                  <ClockRefreshIcon />
                </div>
                <div className="info-content">
                  <div className="info-label">Next billing</div>
                </div>
              </div>
              <div className="info-value">
                Due on{" "}
                {unixToDateTime(
                  stripeSubscription?.current_period_end,
                  FULL_MONTH_YEAR_FORMAT
                ) || "N/A"}
              </div>
            </div>

            <div className="info-card">
              <div className="info-card-content">
                <div className="info-icon">
                  <PercentageIcon />
                </div>
                <div className="info-content">
                  <div className="info-label">Discount applied</div>
                </div>
              </div>
              <div className="info-value">
                {discountPercent ? (
                  <>
                    <span className="discount-badge">20% OFF</span>
                    <span className="discount-text">
                      {currentPlan?.interval?.charAt(0).toUpperCase() +
                        currentPlan?.interval?.slice(1) || "Yearly"}{" "}
                      plan
                    </span>
                  </>
                ) : (
                  "No discount"
                )}
              </div>
            </div>
          </div>

          <Button
            variant="secondary"
            className="update-plan-btn"
            onClick={handleChangePremium}
          >
            Update
          </Button>
        </div>

        {!userDetails?.upcoming_plan && (
          <Button onClick={toggleCancelModal} className="cancel-premium-btn">
            {isStopping ? <Loader className="on-btn" /> : "Cancel Subscription"}
          </Button>
        )}
      </Col>
    );
  };

  const getPurchasePlanUI = () => {
    return (
      <Col md={6} className="price-wrapper">
        {!isCancelledPlan && !isChangePlan && (
          <div className="trial-details">
            <IconGift />
            <div>
              <div className="free-trial">Cancel anytime.</div>
              <div className="sub-text">Start your Surmount Premium plan</div>
            </div>
          </div>
        )}

        <div className="mb-4">
          <div className="fs-5 mb-2">Billing Cycle</div>
          <div>
            {billingCycles.map((interval) => {
              const badge = Object.values(plans).find(
                (p) => p.interval === interval && p.yearly_badge_message
              )?.yearly_badge_message;
              return (
                <Form.Check
                  key={interval}
                  inline
                  name="billingCycle"
                  type="radio"
                  id={`billing-cycle-${interval}`}
                  value={interval}
                  label={
                    <span className="d-flex align-items-center gap-2 flex-wrap">
                      {interval.charAt(0).toUpperCase() + interval.slice(1)}
                      {badge && <Tag label={badge} />}
                    </span>
                  }
                  checked={billingCycle === interval}
                  onChange={handleBillingCycleChange}
                />
              );
            })}
          </div>
        </div>

        <h2 className="mt-3">Select your Plan</h2>
        <Row className="g-3">
          {filteredPlans.map((plan) => {
            const planValue = plan.interval || "free";
            const radioValue = `${plan.id}-${planValue}`;
            const isSelected = selectedPlanId === plan.id;

            return (
              <Col md={6} sm={12} xs={12} key={`${plan.id}-${planValue}`}>
                <Form.Check
                  name="duration"
                  type="radio"
                  value={radioValue}
                  label={
                    <>
                      <div
                        className={`${
                          plan.interval === "yearly"
                            ? "yearly-wrapper"
                            : "monthly-wrapper"
                        }`}
                      >
                        {plan.name}
                      </div>
                      <div className="price">
                        {`$${plan.price} per ${
                          plan.interval === "yearly" ? "year" : "month"
                        }`}
                        {currentPlan?.id === plan.id && (
                          <div className="current-plan-label mt-1">
                            (your current subscription)
                          </div>
                        )}
                      </div>
                    </>
                  }
                  onChange={handleDurationchange}
                  checked={isSelected}
                />
              </Col>
            );
          })}
        </Row>

        <div className="due-date">Due Today: $0.00</div>

        <Row>
          <Col md={isAccountPage ? 12 : 6}>
            <Button
              onClick={handlePurchasePremium}
              disabled={currentPlan?.id === selectedPlan?.id}
              className={classNames({
                disableButton: currentPlan?.id === selectedPlan?.id,
              })}
            >
              {fetching ? <Loader className="on-btn" /> : getActionButtonText()}
            </Button>
          </Col>
          {/* {!isAccountPage && (
            <Col md={6}>
              <Button className="button-outlined" onClick={handleExplore}>
                Let&apos;s explore
              </Button>
            </Col>
          )} */}
        </Row>

        {isAccountPage && !isCancelledPlan && !isChangePlan && (
          <div className="helptext">Cancel any time.</div>
        )}
      </Col>
    );
  };

  const getPlanUI = () => {
    if (stripeSubscription && stripeSubscription?.cancel_at !== null) {
      return (
        <Col className="restore-wrapper">
          <div className="amount-wrapper">
            <span className="amount">${currentPlan?.price || "0"}</span>
            <sub className="duration">
              /{currentPlan?.interval === "monthly" ? "month" : "year"}
            </sub>
          </div>
          <p className="benefit mt-1 mb-2">
            Your subscription will be canceled at{" "}
            {unixToDateTime(
              stripeSubscription?.cancel_at,
              FULL_MONTH_YEAR_FORMAT
            )}
            .
          </p>
          <Button
            className="btn-change-plan"
            onClick={handleContinuePremiumPlan}
          >
            {isUrlFetching ? (
              <Loader className="on-btn" />
            ) : (
              "Restore Subscription"
            )}
          </Button>
        </Col>
      );
    } else if (isCancelPlan) {
      return getCancelPlanUI();
    } else {
      return getPurchasePlanUI();
    }
  };

  return (
    <PurchasePlanCardContainer $planName={currentPlan?.name}>
      {isLoading ? (
        <PurchasePlanModalPlaceholder />
      ) : (
        <Row>
          <div className="content-wrapper">
            {/* <div>
              <p>Invest smarter, not harder</p>
              <h4>With {selectedPlan?.name} you get:</h4>
              <ul>
                {selectedPlan?.benefits?.map((benefit, index) => {
                  return (
                    <li key={index}>
                      <BlueCheckIcon
                        viewBox="0 0 23 10"
                        stroke="#048AF5"
                        strokeWidth="2"
                      />
                      {benefit.type === "special-text" && (
                        <span
                          className="tag"
                          style={{
                            color: benefit.tag.content_color,
                            backgroundColor: benefit.tag.background_color,
                          }}
                        >
                          {benefit.tag.label}
                        </span>
                      )}
                      <span>{benefit.text}</span>
                    </li>
                  );
                })}
              </ul>
            </div> */}
            {/* <div className="pro-plan-label">
              <a href={PLANS_LINK} target="_blank" rel="noopener noreferrer">
                Professional Plan <IconArrowRightDirection />
              </a>
            </div> */}
          </div>
          {getPlanUI()}
        </Row>
      )}
      {showCancelConfirmModal && (
        <ConfirmationModal
          title="Cancel subscription"
          description="<p class='cancel-description'>Are you sure? All premium feature will restrict to access and manage your trading</p>"
          icon={iconCancelModal}
          confirmButtonText="Cancel now"
          cancelButtonText="Nevermind"
          show={showCancelConfirmModal}
          onHide={toggleCancelModal}
          onConfirm={toggleCancelSubscriptionModal}
        />
      )}

      {showCancelSubscriptionModal && (
        <UnsubscribePlanModal
          show={showCancelSubscriptionModal}
          onHide={toggleCancelSubscriptionModal}
          setStopping={setStopping}
        />
      )}
      <ChoosePlanModal show={choosePlaneModel} onHide={toggleSnaptradeModal} />
    </PurchasePlanCardContainer>
  );
};

export { PurchasePlanCard };

PurchasePlanCard.propTypes = {
  isCancelPlan: PropTypes.bool,
  isChangePlan: PropTypes.bool,
  isAccountPage: PropTypes.bool,
  handleExplore: PropTypes.func,
};
